import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TextField, CircularProgress, Box, InputAdornment } from "@mui/material";
import { MdInfoOutline, MdSearch } from "react-icons/md";
import { useBackendLinksProvider } from "../../../Providers/BackendLinksProvider.tsx";
import { useEndpointProvider } from "../../../Providers/EndpointProvider";
import { useAuthProvider } from "../../../Providers/AuthProvider";
import { useEnvironmentVariablesProvider } from "../../../Providers/EnvironmentVariablesProvider.tsx";
import { DEFAULT_PATIENT_ID_LENGTH, DEFAULT_PATIENT_ID_TYPE } from "../../Data/Visualize/UploadsSubpages/UploadWindows/PatientIDType"
import { MobergFontFamily } from "../../../Components/MobergFont/MobergFont";
import { MobergButton, MobergButtonShape, MobergButtonVariant, MobergTheme } from "../../../Moberg";
import { FRONTEND_LINKS } from "../../../Constants/FrontendLinks";
import RMMobileStickyHeader from "./RMMobileStickyHeader";
import StartMonitoringModal from "../components/Modals/StartMonitoringModal";
import MobergSnackbar from "../../../Components/MobergSnackbar/MobergSnackbar";
import { usePatientValidation } from "../hooks/usePatientValidation";
import { URL_PARAMS, MODAL_TYPE, RM_STEPS } from "../constants";

const RMMobileAddPatient = (props) => {
    const { setNavPage } = props;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const gatewayId = searchParams.get("gateway_id");
    const authProvider = useAuthProvider();
    
    const [activeTab, setActiveTab] = useState(0);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    
    // State for confirmation modal
    const [showStartMonitoringModal, setShowStartMonitoringModal] = useState(false);
    const [gatewayInfo, setGatewayInfo] = useState(null);
    const [patientDetailsForModal, setPatientDetailsForModal] = useState(null);
    
    // State for patient selection tab (tab 1)
    const [patientData, setPatientData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPatients, setTotalPatients] = useState(0);
    const [isLoadingPatients, setIsLoadingPatients] = useState(false);
    const pageSize = 10;

    const handleLogout = () => {
        authProvider.logout();
        navigate("/");
    };
    
    const environmentVariablesProvider = useEnvironmentVariablesProvider();
    const PATIENT_ID_LENGTH = environmentVariablesProvider.environmentVariables.PATIENT_ID_LENGTH || DEFAULT_PATIENT_ID_LENGTH;
    const PATIENT_ID_MIN_LENGTH = Math.min(environmentVariablesProvider.environmentVariables.PATIENT_ID_MIN_LENGTH || PATIENT_ID_LENGTH, PATIENT_ID_LENGTH);
    const PATIENT_ID_TYPE = environmentVariablesProvider.environmentVariables.PATIENT_ID_TYPE || DEFAULT_PATIENT_ID_TYPE;
    
    const { LINKS } = useBackendLinksProvider();
    const endpointProvider = useEndpointProvider();
    
    // Use custom validation hook
    const {
        patientId,
        isLoading,
        validationMessage,
        isValid,
        existingPatientData,
        isPatientAdmitted,
        validatePatientId
    } = usePatientValidation({
        patientIdMinLength: PATIENT_ID_MIN_LENGTH,
        patientIdLength: PATIENT_ID_LENGTH,
        patientIdType: PATIENT_ID_TYPE,
        checkIdentifier: async (value) => {
            return await endpointProvider.post(LINKS.DATA.UPLOAD.CHECK_PATIENT_IDENTIFIER, {
                site_id: environmentVariablesProvider.environmentVariables.SITE_ID,
                project_id: environmentVariablesProvider.environmentVariables.PROJECT_ID,
                patient_identifier: value
            });
        },
        getAttributes: async (patientKey) => {
            return await endpointProvider.post(LINKS.DATA.PROFILING.GET_PATIENT_ATTRIBUTES, {
                patient_primary_key: patientKey
            });
        }
    });

    const fetchGatewayInfo = useCallback(async () => {
        try {
            const res = await endpointProvider.get(LINKS.AGGREGATOR.GET_GATEWAY_INFO, {
                gateway_id: parseInt(gatewayId, 10)
            });
            setGatewayInfo(res);
        } catch (error) {
            console.error("Error fetching gateway info:", error);
        }
    }, [endpointProvider, LINKS.AGGREGATOR.GET_GATEWAY_INFO, gatewayId]);

    useEffect(() => {
        setNavPage(false);
        
        // Sync URL params for cross-platform compatibility
        const urlParams = new URLSearchParams(window.location.search);
        if (!urlParams.has(URL_PARAMS.MODAL)) {
            urlParams.set(URL_PARAMS.MODAL, MODAL_TYPE.REMOTE_MONITORING);
        }
        if (!urlParams.has(URL_PARAMS.STEP)) {
            urlParams.set(URL_PARAMS.STEP, RM_STEPS.ADD_PATIENT);
        }
        window.history.replaceState(null, '', `${window.location.pathname}?${urlParams.toString()}`);
        
        fetchGatewayInfo();
    }, [setNavPage, fetchGatewayInfo]);
    
    // Fetch patients for tab 1 (existing patient selection)
    useEffect(() => {
        if (activeTab !== 1) return;
        
        const fetchPatients = async () => {
            setIsLoadingPatients(true);
            const body = {
                page: currentPage,
                filters: { search_value: searchTerm },
                sort: [],
                page_size: pageSize,
                admission_status: "discharged"
            };

            try {
                const res = await endpointProvider.post(LINKS.DATA.PROFILING.GET_PATIENTS_CLINICAL, body);
                const rawData = res?.data || [];
                const total = res?.total || 0;
                setTotalPatients(total);

                const formattedPatients = rawData.map((patient, index) => ({
                    id: patient.patient_key || `${currentPage}-${index}`,
                    patientId: patient.patient_id || `Unknown-${index + 1}`,
                    mrn: patient.patient_mrn || "N/A",
                    name: patient.patient_name?.trim() ? patient.patient_name : "Unknown",
                }));
                setPatientData(formattedPatients);
                
            } catch (err) {
                console.error(err);
                setErrorMessage("Failed to load patients. Please try again.");
                setPatientData([]);
                setTotalPatients(0);
            } finally {
                setIsLoadingPatients(false);
            }
        };

        const delaySearch = setTimeout(() => {
            fetchPatients();
        }, 300);

        return () => clearTimeout(delaySearch);
    }, [activeTab, currentPage, searchTerm, LINKS.DATA.PROFILING.GET_PATIENTS_CLINICAL, endpointProvider, pageSize]);

    const handlePatientIdChange = async (e) => {
        const value = e.target.value;
        
        try {
            await validatePatientId(value);
        } catch (err) {
            console.error("Error checking patient ID:", err);
            setErrorMessage("Error checking patient ID. Please try again.");
        }
    };

    const handleNext = () => {
        if (!patientId || !isValid) {
            setError(true);
            return;
        }
        setError(false);
        
        // Show confirmation modal (no PHI in URL)
        setPatientDetailsForModal({
            mrn: patientId,
            firstName: firstName,
            lastName: lastName
        });
        setShowStartMonitoringModal(true);
    };

    const handleBack = () => {
        navigate(`${FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN}?modal=rm&step=SELECT_GATEWAY`);
    };

    const handleSwitchToExisting = () => {
        setActiveTab(1);
    };
    
    const handleSwitchToAddNew = () => {
        setActiveTab(0);
    };
    
    const handleSearchChange = (value) => {
        setSearchTerm(value);
        setCurrentPage(0); // Reset to first page on search
    };
    
    const handleSelectPatientFromList = (patient) => {
        // Show confirmation modal (no PHI in URL)
        const patientName = patient.name !== "Unknown" ? patient.name : "";
        const nameParts = patientName.split(/\s+/);
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";
        
        setPatientDetailsForModal({
            mrn: patient.patientId,
            firstName: firstName,
            lastName: lastName
        });
        setShowStartMonitoringModal(true);
    };

    const handleSelectExistingPatient = () => {
        if (!existingPatientData) return;
        
        // Extract first and last name from patient_name
        const nameParts = existingPatientData.patient_name?.split(/\s+/) || [];
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";
        
        // Show confirmation modal (no PHI in URL)
        setPatientDetailsForModal({
            mrn: existingPatientData.patient_id,
            firstName: firstName,
            lastName: lastName
        });
        setShowStartMonitoringModal(true);
    };

    const tabStyle = (isActive) => ({
        padding: "14px 16px",
        fontSize: "14px",
        fontWeight: isActive ? "600" : "400",
        color: isActive ? "#0A84FF" : "#5B5B5B",
        background: "none",
        border: "none",
        borderBottom: isActive ? "3px solid #0A84FF" : "3px solid transparent",
        cursor: "pointer",
        fontFamily: MobergFontFamily.REGULAR,
        transition: "all 0.2s ease",
        flex: 1
    });

    const isNextDisabled = !patientId || isLoading || !isValid;

    return (
        <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#ffffff",
            overflow: "hidden"
        }}>
            <RMMobileStickyHeader 
                title="Assign a patient"
                showBackButton={true}
                onBack={handleBack}
                onLogout={handleLogout}
            />

            {/* Tabs */}
            <div style={{ 
                display: "flex", 
                borderBottom: "1px solid #E5E5E5",
                backgroundColor: "#ffffff",
                flexShrink: 0,
                paddingTop: '60px'
            }}>
                <button 
                    style={tabStyle(activeTab === 0)}
                    onClick={handleSwitchToAddNew}
                >
                    Admit new patient
                </button>
                <button 
                    style={tabStyle(activeTab === 1)}
                    onClick={handleSwitchToExisting}
                >
                    Assign existing
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === 0 ? (
                // Tab 0: Admit new patient form
                <div style={{
                    flex: 1,
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch"
                }}>
                    {/* Form Content */}
                    <div style={{
                        padding: '16px'
                    }}>
                    {/* Instructions */}
                    <div style={{
                        backgroundColor: '#F5F5F5',
                        borderRadius: '4px',
                        padding: '12px 14px',
                        fontSize: '13px',
                        color: '#1F2937',
                        lineHeight: '1.5',
                        marginBottom: '16px'
                    }}>
                    Enter the patient's information below to create a new patient record. Patient ID is required and must be unique.
                </div>

                {/* Form Card */}
                <div style={{ 
                    backgroundColor: "white",
                    borderRadius: "4px",
                    padding: "20px",
                    border: "1px solid #E5E5E5"
                }}>
                    <h3 style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#333",
                        margin: "0 0 4px 0",
                        fontFamily: MobergFontFamily.HEADER
                    }}>
                        Patient Information
                    </h3>
                    <p style={{
                        fontSize: "12px",
                        color: "#666",
                        margin: "0 0 16px 0",
                        fontFamily: MobergFontFamily.REGULAR
                    }}>
                        Patient ID is required.
                    </p>
                    
                    {/* Divider */}
                    <div style={{ 
                        borderBottom: "1px solid #E5E5E5", 
                        marginBottom: "20px" 
                    }} />

                    {/* Patient ID */}
                    <div style={{ marginBottom: "20px" }}>
                        <label style={{ 
                            fontSize: "14px", 
                            fontWeight: "600", 
                            color: "#333", 
                            display: "block", 
                            marginBottom: "8px",
                            fontFamily: MobergFontFamily.REGULAR
                        }}>
                            Patient ID <span style={{ color: "red" }}>*</span>
                        </label>
                        <TextField
                            placeholder={PATIENT_ID_MIN_LENGTH === PATIENT_ID_LENGTH
                                ? `${PATIENT_ID_LENGTH} characters required`
                                : `${PATIENT_ID_MIN_LENGTH}-${PATIENT_ID_LENGTH} characters required`}
                            variant="outlined"
                            fullWidth
                            value={patientId}
                            onChange={(e) => handlePatientIdChange(e)}
                            inputProps={{ 
                                maxLength: PATIENT_ID_LENGTH,
                                style: { fontSize: "16px" }
                            }}
                            error={error || (!isValid && validationMessage !== "")}
                            helperText={
                                <span style={{
                                    color: error ? 'red' :
                                        (validationMessage === "Patient ID is available" ? 'green' : 'red'),
                                    fontWeight: "400",
                                    fontSize: "12px",
                                    visibility: (error || validationMessage) ? 'visible' : 'hidden'
                                }}>
                                    {error ? "Patient ID is required" : (validationMessage || "Placeholder")}
                                </span>
                            }
                            InputProps={{
                                endAdornment: isLoading && (
                                    <Box display="flex" alignItems="center" mr={1}>
                                        <CircularProgress size={20} />
                                    </Box>
                                ),
                                style: {
                                    minHeight: "48px"
                                }
                            }}
                        />
                    </div>

                    {/* Existing Patient Selection Card */}
                    {existingPatientData && (
                        <div style={{
                            backgroundColor: "#F0F4F8",
                            borderRadius: "4px",
                            padding: "14px 16px",
                            marginBottom: "20px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px"
                        }}>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                <MdInfoOutline size={20} style={{ color: "#0A84FF", marginTop: "2px", flexShrink: 0 }} />
                                <div style={{ flex: 1 }}>
                                    <p style={{
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        color: "#1F2937",
                                        margin: "0 0 4px 0",
                                        fontFamily: MobergFontFamily.REGULAR,
                                        lineHeight: "1.4"
                                    }}>
                                        Patient {existingPatientData.patient_id} already exists
                                    </p>
                                    <p style={{
                                        fontSize: "13px",
                                        color: "#4B5563",
                                        margin: "0 0 8px 0",
                                        fontFamily: MobergFontFamily.REGULAR,
                                        lineHeight: "1.5"
                                    }}>
                                        <strong>Patient name:</strong> {existingPatientData.patient_name}
                                    </p>
                                    <p style={{
                                        fontSize: "12px",
                                        color: "#6B7280",
                                        margin: "0",
                                        fontFamily: MobergFontFamily.REGULAR,
                                        lineHeight: "1.5"
                                    }}>
                                        Would you like to assign this patient to the selected MCP device?
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleSelectExistingPatient}
                                style={{
                                    width: "100%",
                                    padding: "12px",
                                    backgroundColor: "#007bff",
                                    color: "white",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontFamily: MobergFontFamily.REGULAR,
                                    minHeight: "44px"
                                }}
                            >
                                Select Patient
                            </button>
                        </div>
                    )}

                    {/* Divider */}
                    <div style={{ 
                        borderBottom: "1px solid #E5E5E5", 
                        marginBottom: "12px" 
                    }} />
                    
                    <p style={{
                        fontSize: "12px",
                        color: "#666",
                        margin: "0 0 16px 0",
                        fontFamily: MobergFontFamily.REGULAR
                    }}>
                        Optional
                    </p>

                    {/* First Name */}
                    <div style={{ marginBottom: "20px" }}>
                        <label style={{ 
                            fontSize: "14px", 
                            fontWeight: "600", 
                            color: "#333", 
                            display: "block", 
                            marginBottom: "8px",
                            fontFamily: MobergFontFamily.REGULAR
                        }}>
                            First name
                        </label>
                        <TextField
                            placeholder="Enter first name"
                            variant="outlined"
                            fullWidth
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            inputProps={{
                                style: { fontSize: "16px" }
                            }}
                            InputProps={{
                                style: {
                                    minHeight: "48px"
                                }
                            }}
                        />
                    </div>

                    {/* Last Name */}
                    <div>
                        <label style={{ 
                            fontSize: "14px", 
                            fontWeight: "600", 
                            color: "#333", 
                            display: "block", 
                            marginBottom: "8px",
                            fontFamily: MobergFontFamily.REGULAR
                        }}>
                            Last name
                        </label>
                        <TextField
                            placeholder="Enter last name"
                            variant="outlined"
                            fullWidth
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            inputProps={{
                                style: { fontSize: "16px" }
                            }}
                            InputProps={{
                                style: {
                                    minHeight: "48px"
                                }
                            }}
                        />
                    </div>
                </div>
                </div>
            </div>
            ) : (
                // Tab 1: Assign existing patient
                <div style={{
                    flex: 1,
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch"
                }}>
                    {/* Search */}
                    <div style={{ 
                        padding: '16px'
                    }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by patient ID or name"
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <MdSearch size={20} color="#666" />
                                    </InputAdornment>
                                ),
                                style: {
                                    backgroundColor: "#F5F5F5",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    minHeight: "44px"
                                }
                            }}
                        />
                    </div>

                    {/* Patient Table */}
                    <div style={{ 
                        padding: '0 16px 16px'
                    }}>
                        {isLoadingPatients ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                <CircularProgress />
                            </Box>
                        ) : patientData.length === 0 ? (
                            <div style={{
                                textAlign: "center",
                                padding: "40px 20px",
                                backgroundColor: "white",
                                borderRadius: "12px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                            }}>
                                <div style={{
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    color: "#333",
                                    marginBottom: "8px"
                                }}>
                                    No patients found
                                </div>
                                <div style={{
                                    fontSize: "14px",
                                    color: "#666"
                                }}>
                                    {searchTerm ? "Try a different search term" : "No discharged patients available"}
                                </div>
                            </div>
                        ) : (
                            <div style={{
                                backgroundColor: "white",
                                borderRadius: "4px",
                                overflow: "hidden",
                                border: "1px solid #E5E5E5"
                            }}>
                                {/* Table Header */}
                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "80px 1fr 1fr",
                                    gap: "8px",
                                    padding: "12px",
                                    backgroundColor: "#F8F9FA",
                                    borderBottom: "1px solid #E5E5E5",
                                    fontWeight: "600",
                                    fontSize: "13px",
                                    color: "#666",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 1
                                }}>
                                    <div></div>
                                    <div>Patient ID</div>
                                    <div>Name</div>
                                </div>

                                {/* Table Rows */}
                                {patientData.map((patient) => (
                                    <div 
                                        key={patient.id}
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "80px 1fr 1fr",
                                            gap: "8px",
                                            padding: "12px",
                                            borderBottom: "1px solid #F0F0F0",
                                            alignItems: "center",
                                            fontSize: "14px"
                                        }}
                                    >
                                        <div style={{ display: "flex", justifyContent: "center" }}>
                                            <MobergButton
                                                variant={MobergButtonVariant.CLEAR}
                                                theme={MobergTheme.BLUE}
                                                shape={MobergButtonShape.RECTANGLE}
                                                onClick={() => handleSelectPatientFromList(patient)}
                                                style={{ 
                                                    height: '36px',
                                                    padding: "0 12px",
                                                    fontSize: "13px",
                                                    minWidth: "70px"
                                                }}
                                            >
                                                Select
                                            </MobergButton>
                                        </div>
                                        <div style={{
                                            fontWeight: "600",
                                            color: "#2C2C2C",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                        }}>
                                            {patient.patientId}
                                        </div>
                                        <div style={{
                                            color: "#2C2C2C",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                        }}>
                                            {patient.name}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Bottom Action Button or Pagination */}
            {activeTab === 0 ? (
                <div style={{
                    padding: "16px",
                    backgroundColor: "white",
                    borderTop: "1px solid #E5E5E5",
                    flexShrink: 0
                }}>
                    <button
                        onClick={handleNext}
                        disabled={isNextDisabled}
                        style={{
                            width: "100%",
                            padding: "14px",
                            backgroundColor: isNextDisabled ? "#CCCCCC" : "#007bff",
                            color: "white",
                            fontSize: "16px",
                            fontWeight: "600",
                            border: "none",
                            borderRadius: "4px",
                            cursor: isNextDisabled ? "not-allowed" : "pointer",
                            fontFamily: MobergFontFamily.REGULAR,
                            minHeight: "48px"
                        }}
                    >
                        Next
                    </button>
                </div>
            ) : (
                // Pagination for tab 1
                !isLoading && patientData.length > 0 && totalPatients > 0 && (
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        backgroundColor: "#ffffff",
                        borderTop: "1px solid #E5E5E5",
                        boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
                        flexShrink: 0,
                        position: "relative",
                        zIndex: 10
                    }}>
                        <MobergButton
                            variant={MobergButtonVariant.OUTLINE}
                            theme={MobergTheme.BLUE}
                            shape={MobergButtonShape.RECTANGLE}
                            onClick={() => setCurrentPage(prev => prev - 1)}
                            disabled={currentPage === 0}
                            style={{ 
                                padding: "10px 16px",
                                fontSize: "14px",
                                minWidth: "90px",
                                opacity: currentPage === 0 ? 0.5 : 1
                            }}
                        >
                            Previous
                        </MobergButton>
                        
                        <div style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#2C2C2C",
                            fontFamily: MobergFontFamily.REGULAR
                        }}>
                            Page {currentPage + 1} of {Math.ceil(totalPatients / pageSize)}
                        </div>
                        
                        <MobergButton
                            variant={MobergButtonVariant.OUTLINE}
                            theme={MobergTheme.BLUE}
                            shape={MobergButtonShape.RECTANGLE}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            disabled={currentPage >= Math.ceil(totalPatients / pageSize) - 1}
                            style={{ 
                                padding: "10px 16px",
                                fontSize: "14px",
                                minWidth: "90px",
                                opacity: currentPage >= Math.ceil(totalPatients / pageSize) - 1 ? 0.5 : 1
                            }}
                        >
                            Next
                        </MobergButton>
                    </div>
                )
            )}
            
            {/* Start Monitoring Confirmation Modal */}
            <StartMonitoringModal
                isOpen={showStartMonitoringModal}
                gatewayId={gatewayId}
                gatewayInfo={gatewayInfo}
                patientId={patientDetailsForModal?.mrn}
                patientDetails={patientDetailsForModal}
                onConfirm={() => {
                    setShowStartMonitoringModal(false);
                    navigate(`${FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN}?modal=rm&step=MONITORING&gateway_id=${gatewayId}`);
                }}
                onCancel={() => setShowStartMonitoringModal(false)}
                endpointProvider={endpointProvider}
                LINKS={LINKS}
                authProvider={authProvider}
                siteId={environmentVariablesProvider.environmentVariables.SITE_ID}
                projectId={environmentVariablesProvider.environmentVariables.PROJECT_ID}
            />
            
            {/* Error Notification */}
            <MobergSnackbar
                open={!!errorMessage}
                onClose={() => setErrorMessage("")}
                message={errorMessage}
                severity="error"
            />
        </div>
    );
};

export default RMMobileAddPatient;

